import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Search, Loader2, Eye, Download, FileText, ReceiptText, AlertTriangle, X, Archive } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, apiErrorMessage } from "@/lib/api";
import { PdfViewer } from "./PdfViewer";

const GRUPO = {
  holded: { label: "Holded", cls: "border-emerald-200 bg-emerald-50 text-emerald-700" },
  retencion: { label: "Retención (IRPF)", cls: "border-amber-200 bg-amber-50 text-amber-700" },
  pendiente: { label: "Pendiente", cls: "border-rose-200 bg-rose-50 text-rose-700" },
  historico: { label: "Histórico SOFI 1.0", cls: "border-slate-300 bg-slate-100 text-slate-700" },
};

function FacturaPdf({ item, onClose }) {
  const [pdf, setPdf] = useState(null);
  const [err, setErr] = useState(false);
  React.useEffect(() => {
    let cancelled = false;
    api.get(`/contabilidad/pdf/${item.file_id}`, { responseType: "arraybuffer" })
      .then((res) => { if (!cancelled) setPdf(new Uint8Array(res.data)); })
      .catch(() => { if (!cancelled) setErr(true); });
    return () => { cancelled = true; };
  }, [item.file_id]);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl p-0 gap-0" data-testid="buscador-pdf-dialog">
        <DialogHeader className="flex-row items-center justify-between border-b px-4 py-2.5">
          <DialogTitle className="text-sm">{item.filename || "Factura"} · {item.week_label}</DialogTitle>
          <Button size="icon" variant="ghost" className="h-7 w-7" onClick={onClose}><X className="h-4 w-4" /></Button>
        </DialogHeader>
        <div className="h-[72vh] bg-muted/30">
          {err ? <div className="flex h-full items-center justify-center text-sm text-muted-foreground">No se pudo cargar el PDF.</div> : <PdfViewer data={pdf} loading={!pdf} />}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function CierreBuscador() {
  const [term, setTerm] = useState("");
  const [q, setQ] = useState("");
  const [pdfItem, setPdfItem] = useState(null);

  const resQ = useQuery({
    queryKey: ["conta-buscar", q],
    queryFn: async () => (await api.get("/contabilidad/buscar", { params: { q } })).data,
    enabled: q.trim().length >= 2,
  });

  const items = resQ.data?.items || [];

  const submit = (e) => { e.preventDefault(); setQ(term.trim()); };

  const downloadZip = async (cid, week) => {
    try {
      const res = await api.get(`/contabilidad/cierres/${cid}/zip`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `Cierre_${week || cid}.zip`; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { toast.error(apiErrorMessage(e)); }
  };

  return (
    <div className="space-y-4" data-testid="conta-buscador">
      <Card><CardContent className="p-4">
        <form onSubmit={submit} className="flex flex-wrap items-center gap-2">
          <div className="flex flex-1 items-center gap-2 rounded-lg border bg-white px-3 py-2">
            <Search className="h-4 w-4 text-muted-foreground" />
            <input
              value={term}
              onChange={(e) => setTerm(e.target.value)}
              placeholder="Buscar por proveedor, NIF, nº de factura, concepto, importe…"
              className="w-full bg-transparent text-sm outline-none"
              data-testid="buscador-input"
            />
          </div>
          <Button type="submit" disabled={term.trim().length < 2} data-testid="buscador-submit">
            <Search className="mr-1.5 h-4 w-4" />Buscar
          </Button>
        </form>
        <p className="mt-2 flex items-center gap-1.5 text-xs text-muted-foreground">
          <Archive className="h-3.5 w-3.5" />
          Busca en el archivo de <strong>todas las semanas cerradas</strong> (líneas de los Excel + facturas). Útil para responder a Hacienda.
        </p>
      </CardContent></Card>

      {q.trim().length >= 2 && (
        resQ.isLoading ? <Skeleton className="h-40 w-full" /> : (
          <>
            <p className="text-xs text-muted-foreground" data-testid="buscador-count">
              {items.length} resultado(s) para “{q}”{items.length >= 500 ? " (mostrando los primeros 500)" : ""}
            </p>
            {items.length === 0 ? (
              <Card><CardContent className="py-12 text-center text-sm text-muted-foreground" data-testid="buscador-empty">
                Sin coincidencias en el archivo contable.
              </CardContent></Card>
            ) : (
              <div className="overflow-x-auto rounded-xl border">
                <table className="w-full border-collapse text-left text-sm">
                  <thead className="bg-zinc-50">
                    <tr className="border-b text-[10px] uppercase tracking-wide text-muted-foreground">
                      <th className="px-3 py-2">Semana</th>
                      <th className="px-3 py-2">Tipo / País</th>
                      <th className="px-3 py-2">Proveedor</th>
                      <th className="px-3 py-2">Nº factura</th>
                      <th className="px-3 py-2">Fecha</th>
                      <th className="px-3 py-2">NIF</th>
                      <th className="px-3 py-2 text-right">Importe</th>
                      <th className="px-3 py-2" />
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((r, i) => {
                      const G = GRUPO[r.grupo] || GRUPO.holded;
                      return (
                        <tr key={i} className="border-b hover:bg-zinc-50/60" data-testid={`buscador-row-${i}`}>
                          <td className="whitespace-nowrap px-3 py-2"><Badge className="bg-primary text-[10px] text-primary-foreground">{r.week_label}</Badge></td>
                          <td className="whitespace-nowrap px-3 py-2">
                            <Badge variant="outline" className={`text-[9px] ${G.cls}`}>
                              {r.grupo === "pendiente" && <AlertTriangle className="mr-0.5 h-2.5 w-2.5" />}{G.label}
                            </Badge>
                            {r.pais && <span className="ml-1 text-xs text-muted-foreground">{r.pais}</span>}
                          </td>
                          <td className="max-w-[220px] truncate px-3 py-2" title={r.proveedor || r.ref}>{r.proveedor || r.ref || "—"}</td>
                          <td className="whitespace-nowrap px-3 py-2 tabular-nums">{r.num_factura || "—"}</td>
                          <td className="whitespace-nowrap px-3 py-2 tabular-nums">{r.fecha || "—"}</td>
                          <td className="whitespace-nowrap px-3 py-2 tabular-nums">{r.nif || "—"}</td>
                          <td className="whitespace-nowrap px-3 py-2 text-right tabular-nums">{r.importe ? `${r.importe} ${r.moneda || ""}` : "—"}</td>
                          <td className="whitespace-nowrap px-3 py-2">
                            <div className="flex items-center justify-end gap-1">
                              {r.file_id && (
                                <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={() => setPdfItem(r)} data-testid={`buscador-pdf-${i}`}>
                                  <Eye className="mr-1 h-3.5 w-3.5" />Factura
                                </Button>
                              )}
                              {r.cierre_id && (
                                <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={() => downloadZip(r.cierre_id, r.week_label)} data-testid={`buscador-zip-${i}`}>
                                  <Download className="mr-1 h-3.5 w-3.5" />ZIP semana
                                </Button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )
      )}

      {pdfItem && <FacturaPdf item={pdfItem} onClose={() => setPdfItem(null)} />}
    </div>
  );
}
